/**
 * toastPolicy.js — что программе позволено выводить на экран всплывающим окном.
 *
 * Почему это отдельное правило, а не просто «убрать три вызова».
 *
 * Врач открывает исследование. Пока он ждёт снимки, поверх экрана лезут:
 * «💾 Кеш SW: 0.0 MB», «2D Stack», «⚠️ Высокая нагрузка на ОЗУ (85.3%)!
 * Очистка старых данных…». Он их закрывает — и за это время проходит
 * заставка, которую он хотел увидеть, а потом эти же сообщения ложатся на
 * снимок во время работы. Ни одно из них не отвечает на вопрос, который у
 * человека есть. Это программа рассказывает о себе.
 *
 * Убирать их по одному бесполезно: их пишут в разных файлах разные части
 * программы, и через версию вылезает следующее. Поэтому правило вынесено
 * сюда, а заслон стоит в самой showToast: теперь неважно, кто и откуда
 * позвал, служебная болтовня на экран не попадёт.
 *
 * Граница простая: всплывающее окно оправдано, если оно отвечает на действие
 * ЧЕЛОВЕКА — нажал «Сохранить», получил «Сохранено»; нажал «Измерить», а
 * измерить нечем. Всё, что программа сообщает о собственном устройстве —
 * размер кэша, смена внутреннего режима, давление на память, версия сборки,
 * состояние service worker, — это запись в журнал, а не сообщение врачу.
 *
 * [10.19.44] И второе, что НЕ фильтруется: ПРЯМОЙ ОТВЕТ НА НАЖАТИЕ. Владелец
 * нашёл случай, где заслон задушил ровно то, ради чего сам и писался
 * наоборот: кнопка «Полная диагностика системы» в нижней строке молчала —
 * её сводка перечисляет возможности браузера по именам, среди них
 * sharedArrayBuffer, а правило ниже ловит это слово (оно ставилось против
 * непрошеного «Объём будет собираться медленнее: браузер не выдал общую
 * память»). В браузере без кросс-изоляции SharedArrayBuffer недоступен, в
 * список не попадал — и кнопка «работала»; в сборке Электрона он доступен,
 * слово появлялось, и ответ на нажатие исчезал начисто. Философия этого
 * файла разделяла два случая с первого дня («окно оправдано, если отвечает
 * на действие ЧЕЛОВЕКА»), а код — нет. Теперь разделяет: вызов с пометкой
 * ПРЯМОЙ_ОТВЕТ не фильтруется никогда.
 *
 * Что НЕ фильтруется, нарочно: ошибки. Если что-то сломалось, человек должен
 * узнать об этом, даже если формулировка техническая. Молчащая программа с
 * неправильной картинкой опаснее назойливой.
 *
 * Файл без импортов — чтобы правило проверялось тестом, а не запуском.
 */

/**
 * Служебные темы. Каждая строка — с примером живого сообщения, чтобы через
 * год было видно, от чего именно закрывались, и случайно не сняли лишнее.
 */
export const CHATTER = Object.freeze([
  // «💾 Кеш SW: 0.0 MB» — размер кэша service worker при запуске.
  { re: /кеш\s*sw|кэш\s*sw|cache\s*sw|sw[- ]?cache/i, why: 'размер кэша service worker' },
  // «⚠️ Высокая нагрузка на ОЗУ (85.3%)! Очистка старых данных...»
  { re: /нагрузка на озу|нагрузк\w* на память|memory pressure|очистка старых данных/i, why: 'давление на память — работа сторожа, не дело врача' },
  // «2D Stack», «Axial», «3D Volume» — смена внутреннего режима по кнопке,
  // которую человек только что сам и нажал: он уже видит результат в окне.
  { re: /^(2d stack|axial|sagittal|coronal|3d volume|mpr)$/i, why: 'смена режима — видна в самом окне' },
  // «Mikhmel Cornerstone4D v10.3.2 — Ready» и родня.
  { re: /\bv?\d+\.\d+\.\d+\b.*\b(ready|готов)/i, why: 'номер версии — он написан на заставке' },
  // «Service Worker зарегистрирован», «SW обновлён»
  { re: /service\s*worker|\bsw\b.*(зарегистр|обнов|активир)/i, why: 'жизнь service worker' },
  // «Объём будет собираться медленнее: браузер не выдал общую память» —
  // зависит от заголовков сервера, а не от человека за экраном.
  { re: /общую память|sharedarraybuffer|собираться медленнее/i, why: 'настройка сервера, человек на неё не влияет' },
]);

/**
 * Пропускать ли сообщение на экран.
 *
 * @param {string} message текст
 * @param {string} [type] 'info' | 'success' | 'warning' | 'error'
 * @returns {{allow: boolean, why: string}}
 */
/** [10.19.44] Пометка «это ответ на прямое нажатие»: заслон её пропускает. */
export const ПРЯМОЙ_ОТВЕТ = 'прямой-ответ';

export function judgeToast(message, type = 'info', повод = '') {
  const text = String(message == null ? '' : message).trim();
  if (!text) return { allow: false, why: 'пустое сообщение' };

  // Человек нажал — человек получает ответ. Никакие правила болтовни здесь
  // не применяются: болтовня — это когда программа говорит НЕПРОШЕНО.
  if (повод === ПРЯМОЙ_ОТВЕТ) return { allow: true, why: 'ответ на прямое нажатие' };

  // Ошибку показываем всегда: цена пропущенной ошибки выше цены лишнего окна.
  if (type === 'error') return { allow: true, why: 'ошибка — показываем всегда' };

  // Сравниваем текст без значков: «💾 Кеш SW» и «Кеш SW» — одно и то же.
  const bare = text.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}←-⇿☀-➿️]/gu, '').trim();

  for (const { re, why } of CHATTER) {
    if (re.test(bare) || re.test(text)) return { allow: false, why };
  }
  return { allow: true, why: 'ответ на действие человека' };
}

export default { CHATTER, judgeToast };
